import { Link } from "react-router-dom";
import { ArrowUpRight, TrendingUp, Repeat, Target } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal, MaskedLine } from "../components/Reveal";

const beliefs = [
  { icon: TrendingUp, title: "Ton expertise vaut plus que ton TJM", text: "Le tarif journalier mesure ta disponibilité, pas ta valeur. La bascule consiste à vendre l'impact, pas les heures." },
  { icon: Repeat, title: "Un système bat le talent isolé", text: "Les freelances qui s'en sortent ne sont pas les meilleurs techniquement : ce sont ceux qui ont construit une machine d'acquisition et d'offre." },
  { icon: Target, title: "La sécurité n'est pas dans la régie", text: "Un seul client, un seul canal, zéro actif : la régie est le modèle le plus risqué qui soit. La vraie sécurité, c'est un pipeline qui t'appartient." },
];

export default function APropos() {
  return (
    <div data-testid="about-page" className="bg-[#0A0C10]">
      <Navbar />

      <section className="relative pt-40 pb-20 overflow-hidden grain">
        <div className="absolute top-[-30%] left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-primary/[0.07] blur-[120px] pointer-events-none" />
        <div className="container-site relative">
          <p className="eyebrow mb-6">À propos</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-4xl">
            <MaskedLine delay={0.1}>J'ai fait la bascule.</MaskedLine>
            <MaskedLine delay={0.25} className="text-primary text-glow">Maintenant, je la construis avec toi.</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#0D1017] border-y border-[#262B3D]">
        <div className="container-site grid lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
          <Reveal>
            <div className="glass-card overflow-hidden grain relative">
              <img
                src="/simon-cadart.jpg"
                alt="Simon Cadart, fondateur de La Bascule"
                data-testid="about-portrait"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0A0C10] to-transparent p-6 pt-16">
                <p className="font-heading font-bold text-lg">Simon Cadart</p>
                <p className="text-xs font-mono text-primary uppercase tracking-widest">Fondateur — La Bascule</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="eyebrow mb-4">Le parcours</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mb-6 leading-[1.15]">
              De la régie en ESN au système qui tourne sans moi
            </h2>
            <div className="space-y-4 text-slate-400 text-base leading-relaxed">
              <p>
                J'ai commencé comme beaucoup de freelances tech : des missions en régie enchaînées via des ESN,
                un TJM correct, et cette sensation tenace de tourner dans une roue. Plus je devenais bon,
                plus je remplissais mes journées — sans jamais construire quoi que ce soit qui m'appartienne.
              </p>
              <p>
                Le point de bascule : une mission coupée du jour au lendemain, 80% de mon CA envolé.
                J'ai compris que le problème n'était pas mon niveau technique, mais mon modèle.
                J'ai passé 18 mois à packager mon expertise, construire mon acquisition et signer mes premiers forfaits.
              </p>
              <p>
                Aujourd'hui, j'accompagne les freelances tech qui veulent faire le même chemin —
                sans les 18 mois d'errance. La Bascule, c'est la méthode que j'aurais voulu avoir :
                directe, chiffrée, orientée exécution.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/offre#postuler"
                data-testid="about-cta-apply"
                className="group inline-flex items-center gap-2 bg-primary text-[#0A0C10] font-semibold px-7 py-3.5 rounded-full transition-all duration-300 hover:bg-[#00C885] hover:shadow-[0_0_30px_rgba(0,229,153,0.35)]"
              >
                Travailler avec moi
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/newsletter"
                data-testid="about-cta-newsletter"
                className="inline-flex items-center gap-2 border border-[#262B3D] text-slate-200 font-semibold px-7 py-3.5 rounded-full transition-all duration-300 hover:border-primary hover:text-primary"
              >
                Lire la newsletter
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow mb-4">Les convictions</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Ce en quoi je crois</h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {beliefs.map((b, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div data-testid={`belief-${i}`} className="glass-card p-8 h-full transition-all duration-500 hover:border-primary/50 hover:-translate-y-1">
                  <b.icon className="w-6 h-6 text-primary mb-5" />
                  <h3 className="font-heading text-lg font-bold mb-3 leading-snug">{b.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
